import { AfterViewInit, Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { BuildingMapResult, SearchBuildingsMapParameters } from '@api-proxies';
import { BuildingService } from '@http/clients/building.service';
import { TabNames } from '@models/enums/TabNames';
import { AwaiterService } from '@services/awaiter.service';
import { FilterStateService } from '@services/filter-state.service';
import { MapService, PanelFieldConfig } from '@services/map.service';
import { ICardDetails, MapCardComponent } from '@shared/map-card/map-card.component';
import { CounterBadgeComponent, IconSanitizerService } from 'ngx-lib';
import { Subscription } from 'rxjs';
import { IFilterValueBuildings } from '../buildings-filters/buildings-filter.config';

@Component({
    selector: 'app-buildings-map',
    templateUrl: './buildings-map.component.html',
    styleUrls: ['./buildings-map.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CounterBadgeComponent, MapCardComponent]
})
export class BuildingsMapComponent implements AfterViewInit, OnDestroy, OnInit {
    filters?: IFilterValueBuildings;
    buildings: BuildingMapResult[] = [];
    searchConfig: SearchBuildingsMapParameters = {} as SearchBuildingsMapParameters;
    selectedMarkerData: ICardDetails[] = [];
    buildingsCounter?: number;
    closeIcon?: SafeHtml;
    panelConfig: PanelFieldConfig = {
        mainInfo: [
            { label: 'Address', valueKey: 'fullAddress' },
            {
                label: 'Companies',
                valueKey: 'companyBuildingCount',
                link: { base: '/buildings-single', extra: '/Companies' }
            }
        ],
        tooltip: [
            { label: 'Product Types', valueKey: 'productTypes' },
            { label: 'Alternate Name', valueKey: 'alternateNames' }
        ],
        routerLink: {
            base: '/buildings-single',
            extraKeys: [TabNames[TabNames.Overview]]
        }
    };

    private readonly subscriptions = new Subscription();
    private readonly awaiter = inject(AwaiterService);
    private readonly mapService = inject(MapService);
    private readonly iconSanitizer = inject(IconSanitizerService);
    private readonly filterStateService = inject(FilterStateService<IFilterValueBuildings>);
    private readonly buildingService = inject(BuildingService);

    ngOnInit(): void {
        this.closeIcon = this.iconSanitizer.getIcon('close');
        this.mapService.setPanelConfig(this.panelConfig);
    }

    async ngAfterViewInit(): Promise<void> {
        await this.mapService.initializeMap('buildings');

        const markerSub = this.mapService.panelData$.subscribe(({ items, count }) => {
            this.selectedMarkerData = items;
            this.buildingsCounter = count;
        });

        const refreshSub = this.mapService.refresh$.subscribe(() => this.refreshMapView());

        const filterSub = this.filterStateService.stateFilters$.subscribe(async (newFilters) => {
            this.filters = { ...newFilters };

            this.closePanel();
            this.buildings = [];

            this.refreshMapView();
        });

        this.subscriptions.add(refreshSub);
        this.subscriptions.add(markerSub);
        this.subscriptions.add(filterSub);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    closePanel(): void {
        this.selectedMarkerData = [];
    }

    async refreshMapView(): Promise<void> {
        this.buildSearchConfig();
        await this.awaiter.awaitAction('Loading Buildings', async () => {
            this.buildings = await this.buildingService.searchMap(this.searchConfig);
            this.mapService.fetchData(this.buildings, 'buildings-data');
        });
    }

    private buildSearchConfig(): void {
        const bounds = this.mapService.getMapBounds();
        if (!bounds) return;

        this.searchConfig = {
            latitudeFrom: bounds.getSouth(),
            latitudeTo: bounds.getNorth(),
            longitudeFrom: bounds.getWest(),
            longitudeTo: bounds.getEast(),
            filterText: this.filters?.searchText ?? undefined,
            countryId: this.filters?.country?.id ?? undefined,
            stateId: this.filters?.state?.id ?? undefined,
            cityId: this.filters?.city?.id ?? undefined,
            includeArchived: this.filters?.includeArchived ?? undefined,
            salespersons: this.filters?.salespersons?.length
                ? this.filters?.salespersons.map((s) => s.externalId)
                : undefined
        } as SearchBuildingsMapParameters;
    }
}
